import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import BackspaceOutlinedIcon from '@mui/icons-material/BackspaceOutlined';

interface NumericKeypadProps {
  value: string;
  onChange: (next: string) => void;
  label?: string;
  maxDecimals?: number;
  showValue?: boolean;
}

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', '⌫'] as const;

const NumericKeypad: React.FC<NumericKeypadProps> = ({
  value,
  onChange,
  label,
  maxDecimals = 2,
  showValue = true,
}) => {
  const handleKey = (key: string) => {
    if (key === '⌫') {
      onChange(value.slice(0, -1));
      return;
    }
    if (key === '.') {
      if (maxDecimals <= 0 || value.includes('.')) return;
      onChange(value === '' ? '0.' : `${value}.`);
      return;
    }
    if (value.includes('.')) {
      const decimals = value.split('.')[1] ?? '';
      if (decimals.length >= maxDecimals) return;
    }
    if (value === '0' && key !== '.') {
      onChange(key);
      return;
    }
    onChange(`${value}${key}`);
  };

  return (
    <Box>
      {label ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          {label}
        </Typography>
      ) : null}
      {showValue ? (
        <Box
          sx={{
            bgcolor: '#f0f4f8',
            borderRadius: 2,
            px: 2,
            py: 1.5,
            mb: 1.5,
            textAlign: 'right',
            fontSize: '2rem',
            fontWeight: 700,
            letterSpacing: 1,
            minHeight: 56,
            color: '#1565c0',
          }}
        >
          {value || '0'}
        </Box>
      ) : null}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 1,
        }}
      >
        {KEYS.map((k) => (
          <Button
            key={k}
            variant="contained"
            color={k === '⌫' ? 'inherit' : 'primary'}
            onClick={() => handleKey(k)}
            disabled={k === '.' && maxDecimals <= 0}
            sx={{
              minHeight: 56,
              fontSize: '1.4rem',
              fontWeight: 600,
              borderRadius: 2,
              bgcolor: k === '⌫' ? '#e0e0e0' : undefined,
              color: k === '⌫' ? '#424242' : undefined,
            }}
          >
            {k === '⌫' ? <BackspaceOutlinedIcon /> : k}
          </Button>
        ))}
      </Box>
      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <Button fullWidth variant="outlined" onClick={() => onChange('')}>
          Limpiar
        </Button>
      </Stack>
    </Box>
  );
};

export default NumericKeypad;
